import { useState, useEffect } from 'react';

import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import InfoIcon from '@mui/icons-material/Info';
import { Button } from '@mui/material';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import ButtonActionDeleteEnforceConfirm from 'shared/components/Button/Action/ButtonActionDelete/ButtonActionDeleteEnforceConfirm';
import TableRender, { RenderBoolean, TableRenderFieldT } from 'shared/components/Table/TableRender';
import TabsOrganize, { TabT } from 'shared/components/TabsOrganize';
import Loader from 'shared/containers/Loader';
import ViewDetails, {
  GENERAL_INFO_TITLE, SETTINGS_TITLE, ViewDetailsT,
} from 'shared/containers/ViewDetails/ViewDetails';
import { DeviceConfigT } from 'shared/models/device-configs';
import { DeviceProfileT } from 'shared/models/device-profiles';
import { FlowT } from 'shared/models/flows';
import api from 'utils/api';
import { datePrettyDisplay, formatDateFromNow } from 'utils/date';
import { useEntityNameForNav } from 'utils/hooks/providers/entity-name-for-nav-context';

import './FlowsDetails.scss';

function FlowsDetails() {
  const { flowId } = useParams() as any;
  const history = useHistory();
  const [flowData, setFlowData] = useState<FlowT>({} as FlowT);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const { setEntityNameForNav } = useEntityNameForNav();

  useEffect(() => {
    api.flows.getOne(flowId)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then(() => {
              toast.warn('This flow doesn\'t exists');
              history.push('/flows');
            });
          setIsLoadingDetails(false);
        } else {
          resp.json()
            .then((data: FlowT) => {
              setFlowData(data);
              setEntityNameForNav(data.name);
              setIsLoadingDetails(false);
            });
        }
      });
  }, []);

  const deviceProfilesRenderFields: TableRenderFieldT[] = [
    {
      displayName: 'Name',
      renderFn: (row: DeviceProfileT) => row.name,
    },
    {
      displayName: 'Template',
      renderFn: (row: DeviceProfileT) => row.templateName,
      linkFn: (row: DeviceProfileT) => `/schc-templates/${row.templateId}`,
      align: 'center',
    },
    {
      displayName: 'Nb Devices',
      renderFn: (row: DeviceProfileT) => row.nbrDevices,
      align: 'center',
    },
    {
      displayName: 'Auto Provisioning',
      renderFn: (row: DeviceProfileT) => RenderBoolean(row.autoProvision, 'icon'),
      align: 'center',
    },
    {
      displayName: 'Last Update',
      renderFn: (row: DeviceProfileT) => formatDateFromNow(row.lastUpdated),
      tooltipFn: (row: DeviceProfileT) => datePrettyDisplay(row.lastUpdated),
      align: 'center',
    },
  ];

  const deviceConfigRenderFields: TableRenderFieldT[] = [
    {
      displayName: 'Name',
      renderFn: (row: DeviceConfigT) => row.name,
    },
    {
      displayName: 'Device Profile',
      renderFn: (row: DeviceConfigT) => row.devProfileName,
      linkFn: (row: DeviceConfigT) => `/flows/${flowId}/device-profiles/${row.devProfileId}`,
      align: 'center',
    },
    {
      displayName: 'Device ID',
      renderFn: (row: DeviceConfigT) => row.deviceId,
      align: 'center',
    },
    {
      displayName: 'IP Address',
      renderFn: (row: DeviceConfigT) => row.addressIPv6 || row.addressIPv4,
      align: 'center',
    },
    {
      displayName: 'Last Update',
      renderFn: (row: DeviceConfigT) => formatDateFromNow(row.lastUpdated),
      tooltipFn: (row: DeviceConfigT) => datePrettyDisplay(row.lastUpdated),
      align: 'center',
    },
  ];

  const dataStructureDetails: ViewDetailsT = {
    sections: [
      {
        title: GENERAL_INFO_TITLE,
        items: [
          {
            type: 'name',
          },
          {
            type: 'creation',
          },
          {
            label: 'Nb device profiles',
            dataPath: 'nbDeviceProfiles',
          },
          {
            type: 'update',
          },
          {
            label: 'Synchronization Profile Enabled',
            dataPath: 'enableSync',
          },
          {
            label: 'DTLS Enabled',
            dataPath: 'enableDtls',
          },
        ],
        shape: 2,
      },
      {
        title: SETTINGS_TITLE,
        items: [
          {
            label: 'IP Network',
            dataPath: 'poolName',
            linkFn: (data: FlowT) => `/ip-networks/${data.poolId}`,
          },
          {
            label: 'Source',
            dataPath: 'sourceName',
            linkFn: (data: FlowT) => `/sources/${data.sourceId}`,
          },
        ],
        shape: 1,
      },
    ],
  };

  const tabsContent: TabT[] = [
    {
      label: 'Devices',
      name: 'devices',
      content: (
        <>
          <div className="title__header">
            <h2 className="title__header--block">Devices</h2>
            <div className="title__header--actions">
              <Link to={`/flows/${flowId}/devices/create`}>
                <Button variant="contained" color="secondary" size="small">Create</Button>
              </Link>
            </div>
          </div>
          <TableRender
            api={api.deviceConfig}
            apiParams={{ flowId }}
            renderFields={deviceConfigRenderFields}
            onClickRow={(row: DeviceConfigT) => history.push(`/flows/${flowId}/device-profiles/${row?.devProfileId}/devices/${row.id}`)}
            forceRerender={1}
          />
        </>
      ),
      icon: <FormatListBulletedIcon />,
      iconPosition: 'start',
    },
    {
      label: 'Device Profiles',
      name: 'device-profiles',
      content: (
        <>
          <div className="title__header">
            <h2 className="title__header--block">Profiles</h2>
            <div className="title__header--actions">
              <Link to={`/flows/${flowId}/device-profiles/create`}>
                <Button variant="contained" color="secondary" size="small">Create</Button>
              </Link>
            </div>
          </div>
          <TableRender
            api={api.deviceProfiles}
            apiParams={{ flowId }}
            renderFields={deviceProfilesRenderFields}
            onClickRow={(row: DeviceProfileT) => history.push(`/flows/${flowId}/device-profiles/${row?.id}`)}
          />
        </>
      ),
      icon: <FormatListBulletedIcon />,
      iconPosition: 'start',
    },
    {
      label: 'Details',
      name: 'details',
      content: (
        <div style={{ overflowX: 'scroll' }}>
          <ViewDetails dataStructure={dataStructureDetails} data={flowData} />
        </div>
      ),
      icon: <InfoIcon />,
      iconPosition: 'start',
    },
  ];

  if (isLoadingDetails) {
    return <Loader.Page />;
  }
  return (
    <div>
      <div className="title__header">
        <div className="title__header--block">
          <h1>Flow · {flowData.name}</h1>
        </div>
        <div className="title__header--actions">
          <Link to={`/flows/${flowId}/edit`}>
            <Button variant="contained" color="secondary">Edit</Button>
          </Link>
          <ButtonActionDeleteEnforceConfirm
            targetId={flowId}
            targetName={flowData.name}
            api={api.flows}
            apiCallParams={{ flowId }}
            messageComplement={`the flow named\n'${flowData.name}' with ID '${flowId}' containing ${+flowData.nbDeviceProfiles || 'no'} device profiles`}
            redirectLocation="/flows"
          />
        </div>
      </div>
      <TabsOrganize tabs={tabsContent} />
    </div>
  );
}

export default FlowsDetails;
